<template>
  <LoadingContainer :is-loading="isLoading">
    <form class="has-sticky-submit" @submit.prevent="submit">
      <div class="card">
        <div class="card-header">
          <div class="card-header-title">
            <div class="title is-6 is-marginless">
              Header Links <span class="tag">Επεξεργασία</span>
            </div>
          </div>
          <div class="card-header-icon">
            <div class="field has-addons is-marginless">
              <div class="control">
                <RouterLink
                  :to="{ name: 'content.home.header-links.list' }"
                  class="button"
                >
                  <span class="icon is-small">
                    <i class="fa fa-list" />
                  </span>
                  <span>Λίστα</span>
                </RouterLink>
              </div>
              <div class="control">
                <button type="button" class="button" @click="askToDelete">
                  <span class="icon is-small">
                    <i class="fa fa-trash" />
                  </span>
                  <span>Διαγραφή</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card-content">
          <div class="columns is-multiline">
            <div class="column is-full">
              <div class="field">
                <label class="label">Τίτλος (EL) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_el"
                    class="input"
                    type="text"
                    name="title_el"
                  />
                  <p v-show="errors.has('title_el')" class="help is-danger">
                    Εισάγετε ελληνικό τίτλο
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-full">
              <div class="field">
                <label class="label">Τίτλος (EN) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_en"
                    class="input"
                    type="text"
                    name="title_en"
                  />
                  <p v-show="errors.has('title_en')" class="help is-danger">
                    Εισάγετε αγγλικό τίτλο
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-full">
              <div class="field">
                <label class="label">Link (URL) *</label>
                <div class="control">
                  <input
                    v-validate="'required|url'"
                    v-model.trim="form.link"
                    class="input"
                    type="text"
                    name="link"
                  />
                  <p v-show="errors.has('link')" class="help is-danger">
                    Εισάγετε έγκυρο link
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-full">
              <div class="field">
                <label class="label">
                  Εμφανές *
                  <span class="tag is-rounded ml-2">
                    Max 3 ενεργά
                  </span>
                </label>
                <div class="control">
                  <ToggleButton
                    :sync="true"
                    :value="form.is_active"
                    :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                    :width="30"
                    :height="15"
                    @change="updateActive"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StickyFormFooter>
        <template #left>
          <FormSubmit :is-saving="isSaving" @reset="reset" />
        </template>

        <template v-if="form.link" #right>
          <a v-tooltip="'Προβολή στο e-shop'" :href="form.link" target="_blank">
            Προβολή link στο e-shop <i class="fa fa-arrow-right" />
          </a>
        </template>
      </StickyFormFooter>
    </form>

    <Confirmation
      :model-name="form.title_el"
      :is-open="isDeleteOpen"
      description="Είστε βέβαιος για τη διαγραφή αυτού του link;"
      @closeModal="closeDeleteModal"
    />
  </LoadingContainer>
</template>

<script>
import request from '@/utils/request';
import StickyFormFooter from '@/views/components/StickyFormFooter';
import LoadingContainer from '@/views/components/LoadingContainer';
import FormSubmit from '@/views/components/FormSubmit';
import Confirmation from '@/views/components/Confirmation';

export default {
  name: 'HeaderLinksEdit',
  components: {
    LoadingContainer,
    StickyFormFooter,
    FormSubmit,
    Confirmation,
  },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      isDeleteOpen: false,
      original: null,
      form: {
        id: null,
        title_el: '',
        title_en: '',
        link: '',
        is_active: false,
      },
    };
  },

  metaInfo: { title: 'Header Links - Edit' },

  watch: {
    '$route.params.id': {
      immediate: true,
      async handler() {
        await this.fetchItem();
      },
    },
  },

  methods: {
    async fetchItem() {
      try {
        this.isLoading = true;
        // No show endpoint — fetch all and pick the one by id
        const { data } = await request.get('/home/header-links');
        const id = Number(this.$route.params.id);
        const found =
          (Array.isArray(data?.data) ? data.data : []).find(
            x => Number(x.id) === id,
          ) || null;

        if (!found) {
          this.$notify({
            type: 'error',
            title: 'Σφάλμα',
            text: 'Το link δεν βρέθηκε',
          });
          this.$router
            .push({ name: 'content.home.header-links.list' })
            // eslint-disable-next-line prettier/prettier
            .catch(() => {});
          return;
        }

        this.original = found;
        this.form = {
          id: found.id,
          title_el: found.title_el || '',
          title_en: found.title_en || '',
          link: found.link || '',
          is_active: !!found.is_active,
        };
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: err?.message || 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    async submit() {
      try {
        const isValid = await this.$validator.validateAll();
        if (!isValid) throw new Error('Client-side validation failed');

        this.isSaving = true;

        const { data } = await request.patch(
          `/home/header-links/${this.form.id}`,
          {
            title_el: this.form.title_el,
            title_en: this.form.title_en,
            link: this.form.link,
            is_active: !!this.form.is_active,
          },
        );

        this.isSaving = false;

        if (data?.error) {
          this.$notify({
            type: 'error',
            title: 'Αποτυχία',
            text:
              data.message || 'To αίτημα απέτυχε (ελέγξτε το όριο 3 ενεργών).',
          });
          return;
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Το link αποθηκεύτηκε',
        });

        // refresh local copy
        await this.fetchItem();
      } catch (err) {
        this.isSaving = false;
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: err?.response?.data?.message || 'To αίτημα απέτυχε',
        });
      }
    },

    reset() {
      if (!this.original) return;
      this.form = {
        id: this.original.id,
        title_el: this.original.title_el || '',
        title_en: this.original.title_en || '',
        link: this.original.link || '',
        is_active: !!this.original.is_active,
      };
      this.$validator.reset();
    },

    updateActive({ value }) {
      this.form.is_active = !!value;
    },

    askToDelete() {
      this.isDeleteOpen = true;
    },

    async closeDeleteModal(confirmed) {
      this.isDeleteOpen = false;
      if (!confirmed) return;

      try {
        const { data } = await request.delete(
          `/home/header-links/${this.form.id}`,
        );
        if (data?.error) {
          throw new Error(data.message || 'Server error');
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Το link διαγράφηκε',
        });

        this.$router
          .push({ name: 'content.home.header-links.list' })
          // eslint-disable-next-line prettier/prettier
          .catch(() => {});
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: err?.message || 'To αίτημα απέτυχε',
        });
      }
    },
  },
};
</script>
